/**
 * Newsletter form AJAX submission - submits without page refresh
 */
class NewsletterForm extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('form');
    this.submitButton = this.form?.querySelector('[type="submit"]');
    this.messageContainer = this.querySelector('.newsletter-form__messages');

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }

  onSubmit(event) {
    event.preventDefault();

    if (!this.form || this.submitButton?.disabled) return;

    const formData = new FormData(this.form);
    const originalButtonText = this.submitButton?.textContent;

    // Set loading state
    this.setLoading(true);
    this.clearMessages();

    fetch(this.form.action || '/contact', {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'text/html'
      }
    })
      .then((response) => response.text())
      .then((html) => {
        this.handleResponse(html);
      })
      .catch((error) => {
        console.error('Newsletter form error:', error);
        this.showError('Something went wrong. Please try again.');
      })
      .finally(() => {
        this.setLoading(false);
      });
  }

  setLoading(loading) {
    if (this.submitButton) {
      this.submitButton.disabled = loading;
      this.submitButton.setAttribute('aria-busy', loading);
    }
  }

  clearMessages() {
    if (this.messageContainer) {
      this.messageContainer.innerHTML = '';
      this.messageContainer.hidden = true;
    }
  }

  showSuccess(message) {
    if (!this.messageContainer) return;

    this.messageContainer.innerHTML = `
      <h3
        class="newsletter-form__message newsletter-form__message--success form__message"
        id="ContactFooter-success"
        tabindex="-1"
      >
        <span class="svg-wrapper">
          <svg class="icon icon-success" viewBox="0 0 13 13">
            <path fill="#428445" stroke="#fff" stroke-width=".7" d="M6.5 12.35a5.85 5.85 0 1 0 0-11.7 5.85 5.85 0 0 0 0 11.7Z"></path>
            <path stroke="#fff" d="m5.533 8.664 3.72-3.982M4.106 6.769l2.032 1.857"></path>
          </svg>
        </span>
        ${this.escapeHtml(message)}
      </h3>
    `;
    this.messageContainer.hidden = false;
    this.form?.reset();

    // Update URL to include customer_posted=true (without page refresh)
    const url = new URL(window.location.href);
    url.searchParams.set('customer_posted', 'true');
    window.history.replaceState({}, '', url.toString());
  }

  showError(message) {
    if (!this.messageContainer) return;

    this.messageContainer.innerHTML = `
      <small class="newsletter-form__message form__message" id="ContactFooter-error">
        <span class="svg-wrapper">
          <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" viewBox="0 0 14 14">
            <path d="M7 1.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM0 7a7 7 0 1114 0A7 7 0 010 7z" fill="currentColor"/>
            <path d="M7 4a.75.75 0 01.75.75v2.5a.75.75 0 01-1.5 0v-2.5A.75.75 0 017 4zm0 6a1 1 0 100-2 1 1 0 000 2z" fill="currentColor"/>
          </svg>
        </span>
        ${this.escapeHtml(message)}
      </small>
    `;
    this.messageContainer.hidden = false;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  handleResponse(html) {
    // Shopify returns full page HTML - look for success/error indicators
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');

    // Check for error first
    const errorEl = doc.querySelector('#ContactFooter-error');
    if (errorEl && errorEl.textContent?.trim()) {
      this.showError(errorEl.textContent.trim());
      return;
    }

    // Check for success
    const successEl = doc.querySelector('.newsletter-form__message--success, .form__message--success, .form-success');
    if (successEl) {
      const messageText = successEl.textContent?.trim() || 'Thanks for subscribing';
      this.showSuccess(messageText);
      return;
    }

    // Fallback: check raw HTML for indicators
    if (html.includes('form-success') || html.includes('newsletter-form__message--success') ||
        html.includes('already been subscribed') || html.includes('already subscribed')) {
      this.showSuccess('Thanks for subscribing');
      return;
    }

    // Check for common error phrases in response
    if (html.includes('can\'t be blank') || html.includes('invalid') || html.includes('form__message')) {
      const formMessage = doc.querySelector('.form__message');
      if (formMessage && formMessage.textContent?.trim()) {
        this.showError(formMessage.textContent.trim());
        return;
      }
    }

    // Default to success - Shopify newsletter redirects on success
    this.showSuccess('Thanks for subscribing');
  }
}

customElements.define('newsletter-form', NewsletterForm);
